import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { STATIC_STRINGS } from "../../../utils/Constants";
import GenericToast from "../../../common/GenericToast";

const steps = [
  STATIC_STRINGS.ASSESSMENT_DETAILS,
  STATIC_STRINGS.TOPIC_DISTRIBUTION,
  STATIC_STRINGS.PREVIEW_QUESTIONS,
  STATIC_STRINGS.SCHEDULE_TIMINGS,
  STATIC_STRINGS.ADD_CANDIDATES,
];

const ProgressStepper = ({
  activeStep,
  setActiveStep,
}: {
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const [completed, setCompleted] = useState<number[]>([]);
  const [isToastOpen, setToastOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const newCompleted: number[] = [];
    for (let i = 0; i < activeStep - 1; i++) {
      newCompleted.push(i);
    }
    setCompleted(newCompleted);
  }, [activeStep]);

  const handlePrev = () => {
    setActiveStep(activeStep - 1);
    setCompleted((prevCompleted) =>
      prevCompleted.filter((step) => step !== activeStep - 1)
    );
  };

  const { title, jobDesignation, skills } = useSelector(
    (state: RootState) => state.stepOne
  );

  const validateCurrentStep = () => {
    switch (activeStep) {
      case 1:
        return title && jobDesignation && skills;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateCurrentStep()) {
      setToastOpen(true);
      return;
    }
    if (!completed.includes(activeStep - 1)) {
      setCompleted((prevCompleted) => [...prevCompleted, activeStep - 1]);
    }
    setActiveStep(activeStep + 1);
  };

  return (
    <div className="w-full px-2 xl:px-4 mx-auto h-1/3 flex flex-col justify-around">
      <div className="w-full flex px-4 py-2 justify-between items-center border-b-[1px] border-neutral-dark">
        <div className="w-fit">
          <button
            className="cursor-pointer text-neutral-dark p-1 font-medium text-sm xl:text-base"
            onClick={() => navigate("/assessments")}
          >
            <ArrowBackIcon /> {STATIC_STRINGS.BACK}
          </button>
          <div className="text-2xl xl:text-4xl font-semibold">
            {title || "\xa0"}
          </div>
        </div>
        <div className="w-fit flex items-center justify-end">
          <button
            onClick={handlePrev}
            className={`btn text-sm xl:text-base ${
              activeStep === 1 ? `btn-outline-disabled` : `btn-primary`
            } mr-2`}
            disabled={activeStep === 0}
          >
            <ChevronLeftIcon />
            {STATIC_STRINGS.PREVIOUS_STEP}
          </button>
          <button
            onClick={handleNext}
            className="btn btn-primary text-sm xl:text-base"
          >
            {completed.length === steps.length - 1
              ? STATIC_STRINGS.FINISH
              : STATIC_STRINGS.NEXT_STEP}
            <ChevronRightIcon />
          </button>
        </div>
      </div>
      <div className="p-2">
        <Stepper activeStep={activeStep - 1} alternativeLabel>
          {steps.map((step, index) => (
            <Step
              key={step}
              completed={completed.includes(index)}
              sx={{
                "& .MuiStepLabel-root .Mui-completed": {
                  color: "var(--color-success)",
                },
                "& .MuiStepLabel-root .Mui-active": {
                  color: "var(--color-neutral-dark)",
                },
                "& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel":
                  {
                    color: "var(--color-text)",
                    fontWeight: "bold",
                    fontSize: {
                      xs: "0.875rem",
                      xl: "1rem",
                    },
                  },
                "& .MuiStepLabel-label": {
                  fontSize: {
                    xs: "0.875rem",
                    xl: "1rem",
                  },
                },
                "& .MuiStepLabel-root .Mui-active .MuiStepIcon-text": {
                  fill: "var(--color-white)",
                },
              }}
            >
              <StepLabel>{step}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </div>
      <GenericToast
        open={isToastOpen}
        onCloseHandler={() => setToastOpen(false)}
        label={STATIC_STRINGS.FORM_VALIDATION_ERROR}
      />
    </div>
  );
};

export default ProgressStepper;
